/**
 * Per-user in-memory analysis form drafts (same browser tab).
 * Isolated by user id so another account never restores someone else's draft.
 */

export interface AnalysisDraftSnapshot {
  formData: {
    case_number: string;
    policy_number: string;
    project_number: string;
    address: string;
    date_start: string;
    date_end: string;
    engineer_notes: string;
    structure_type: string;
    total_floors: number;
  };
  currentArea: string;
  imageCollections: { [key: string]: { files: File[]; descriptions: string[] } };
  selectedAudio: File | null;
  selectedWindReport: File | null;
  windReportExtraction: unknown;
  /** Weather verification report text + stats, so the generated report survives
   *  leaving and re-entering the analysis page (same browser tab). */
  weatherGenResultText: string | null;
  /** Structured report payload returned alongside weatherGenResultText.
   *  Sent to /api/weather-verification/report-pdf to render a styled PDF
   *  (Forensic AI cover page, branded tables) consistent with inspection
   *  plan PDFs. Persisted in-memory only, same lifetime as the text. */
  weatherGenResultJson: Record<string, unknown> | null;
  weatherGenStats: {
    stations: number;
    isd_stations: number;
    wind_events: number;
    storm_events: number;
  } | null;
  svgData: string;
  uploadedSvgFile: File | null;
  savedSvgs: Array<{ name: string; data: string; preview: string }>;
  currentStep: 1 | 2 | 3;
  showNotes: boolean;
  showAudioSection: boolean;
  showWindSection: boolean;
  showSvgSection: boolean;
  analysisStartedCaseNumber: string | null;
}

const analysisDraftByUserId = new Map<string, AnalysisDraftSnapshot>();

export function getAnalysisDraft(userId: string): AnalysisDraftSnapshot | null {
  return analysisDraftByUserId.get(userId) ?? null;
}

export function setAnalysisDraft(userId: string, snapshot: AnalysisDraftSnapshot): void {
  analysisDraftByUserId.set(userId, snapshot);
}

/** Call on logout or session end so the previous user's files are dropped from memory. */
export function clearAnalysisDraftForUser(userId: string): void {
  analysisDraftByUserId.delete(userId);
}

export function cloneImageCollections(
  collections: AnalysisDraftSnapshot['imageCollections']
): AnalysisDraftSnapshot['imageCollections'] {
  const out: AnalysisDraftSnapshot['imageCollections'] = {};
  for (const key of Object.keys(collections)) {
    const col = collections[key];
    out[key] = { files: [...col.files], descriptions: [...col.descriptions] };
  }
  return out;
}
